import {AnnoTypeEnum, BaseAnnotation, Size} from '@mydocma/component-library/src/lib/annotation';
import {ScribbleData} from '@docma-mm-core/scribble/scribble';
import {ScribbleConverter} from './scribble.converter';
import {TextConverter} from './text.converter';
import {ArrowConverter} from './arrow.converter';
import {RectangleConverter} from './rectangle.converter';
import {ArrowLegacyConverter} from './arrow-legacy.converter';
import {DetailConverter} from './detail.converter';
import {EllipseConverter} from './ellipse.converter';

export type converterModelToData = (anno: BaseAnnotation, container: Size) => ScribbleData;
export type converterDataToModel = (annoData: ScribbleData, container: Size) => BaseAnnotation;


export class Converters {
  private modelConverters = new Map<AnnoTypeEnum, converterModelToData>([
    [AnnoTypeEnum.Pen,    ScribbleConverter.convertToData],
    [AnnoTypeEnum.Marker, ScribbleConverter.convertToData],
    [AnnoTypeEnum.Text, TextConverter.convertToData],
    [AnnoTypeEnum.Arrow, ArrowConverter.convertToData],
    [AnnoTypeEnum.ArrowLegacy, ArrowLegacyConverter.convertToData],
    [AnnoTypeEnum.Rectangle, RectangleConverter.convertToData],
    [AnnoTypeEnum.Speech, RectangleConverter.convertToData],
    [AnnoTypeEnum.Detail, DetailConverter.convertToData],
    [AnnoTypeEnum.Ellipse, EllipseConverter.convertToData],
  ]);

  private dataConverters = new Map<AnnoTypeEnum, converterDataToModel>([
    [AnnoTypeEnum.Pen,    ScribbleConverter.convertToModel],
    [AnnoTypeEnum.Marker, ScribbleConverter.convertToModel],
    [AnnoTypeEnum.Text, TextConverter.convertToModel],
    [AnnoTypeEnum.Arrow, ArrowConverter.convertToModel],
    [AnnoTypeEnum.ArrowLegacy, ArrowLegacyConverter.convertToModel],
    [AnnoTypeEnum.Rectangle, RectangleConverter.convertToModel],
    [AnnoTypeEnum.Speech, RectangleConverter.convertToModel],
    [AnnoTypeEnum.Detail, DetailConverter.convertToModel],
    [AnnoTypeEnum.Ellipse, EllipseConverter.convertToModel],
  ]);

  supportsScribble(s: ScribbleData): boolean {
    return this.dataConverters.has((s.PinShape as AnnoTypeEnum));
  }

  toModel(s: ScribbleData, container: Size): BaseAnnotation {
    const key = s.PinShape as AnnoTypeEnum;
    const fn = this.dataConverters.get(key);
    if (!fn) { throw new Error(`Unsupported PinShape=${s.PinShape}`); }
    return fn(s, container);
  }

  toData(anno: BaseAnnotation, container: Size): ScribbleData {
    const fn = this.modelConverters.get(anno.type);
    if (!fn) { throw new Error(`Unsupported anno.type=${anno.type}`); }
    return fn(anno, container);
  }
}

export function toARGB(hex: string, opacity: number = 1): string {
  // Clamp opacity between 0 and 1
  opacity = Math.min(1, Math.max(0, opacity));

  // Convert opacity (0–1) to 2-digit hex (00–FF)
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();

  // Remove leading #
  const value = hex.replace(/^#/, '');

  // If already ARGB
  if (value.length === 8) {
    // Replace alpha if opacity provided explicitly
    return `#${alpha}${value.slice(2).toUpperCase()}`;
  }

  // If RGB (#RRGGBB)
  if (value.length === 6) {
    return `#${alpha}${value.toUpperCase()}`;
  }

  throw new Error('Invalid hex color format');
}

export function toRGB(hex: string): string {
  const value = hex.replace(/^#/, '');

  if (value.length === 8) {
    return `#${value.slice(2).toUpperCase()}`;
  }

  if (value.length === 6) {
    return `#${value.toUpperCase()}`;
  }

  throw new Error('Invalid hex color format');
}

export function getOpacityFromARGB(hex: string): number {
  const value = hex.replace(/^#/, '');

  if (value.length !== 8) {
    return 1;
  }

  // Extract AA (alpha) from AARRGGBB
  const alphaHex = value.slice(0, 2);
  const alphaDecimal = parseInt(alphaHex, 16);

  // Convert to opacity between 0 and 1
  return +(alphaDecimal / 255).toFixed(1);
}
